import styled, { css, DefaultTheme, keyframes } from 'styled-components';
import { Icon } from 'src/components';
import { Color } from 'src/utils/theme';

const recordingPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
`;

// Stays modest so the halo clears the hint text below the full-size button and
// is not clipped by the compact widget's container.
const recordingHalo = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(1.15); opacity: 0; }
`;

// The red fill on its own reads as the same visual weight as the idle purple,
// so recording also pulses and emits a halo that stays visible with a finger
// or cursor over the button. The ring is offset from the button edge so it
// still reads as a separate outline when reduced motion freezes it.
const recordingIndicator = ({ theme }: { theme: DefaultTheme }) => css`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -${theme.sz.s1};
    border-radius: inherit;
    border: ${theme.bo.mediumWidth} solid ${theme.co.error50};
    pointer-events: none;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${recordingPulse} 1200ms ease-in-out infinite;

    &::after {
      animation: ${recordingHalo} 1200ms ease-out infinite;
    }
  }
`;

export const InstructionText = styled.div(
  ({ theme }) => css`
    text-align: center;
    color: ${theme.co.secondary20};
    ${theme.typography.heading.desktop.xxs};
  `,
);

export const MainText = styled.div(
  ({ theme }) => css`
    text-align: center;
    color: ${theme.co.secondary0};
    ${theme.typography.heading.desktop.m};
  `,
);

export const SecondaryTargetValue = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.sz.s2};
    text-align: center;
    color: ${theme.co.secondary0};
    ${theme.typography.heading.desktop.xxs};
  `,
);

export const SourceValue = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.sz.s2};
    margin-bottom: ${theme.sz.s6};
    text-align: center;
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary50};

    @media (max-width: ${theme.sz.md}) {
      margin-bottom: ${theme.sz.s0};
    }
  `,
);

// Shared by the target-audio and native-speaker-video buttons: same size and
// colour so both read as secondary, on-demand help alongside the primary
// microphone action.
const SecondaryActionButton = styled.button(
  ({ theme }) => css`
    background-color: ${theme.co.tertiary60};
    color: ${theme.co.secondary0};
    border-radius: ${theme.bo.round};
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    width: ${theme.sz.s16};
    height: ${theme.sz.s16};
    font-size: ${theme.sz.s10};

    svg {
      font-size: 1em;
    }

    &:hover {
      background-color: ${theme.co.tertiary50};
    }
  `,
);

export const AudioButton = SecondaryActionButton;
export const VideoButton = SecondaryActionButton;

export const SecondaryActionsRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s6};
  `,
);

export const VideoModalContent = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};
    max-width: ${theme.sz.md};
    max-width: ${theme.sz.md};
    padding: ${theme.sz.s12} ${theme.sz.s6} ${theme.sz.s6};

    video {
      width: 100%;
      max-height: 70dvh;
    }
  `,
);

// Always full size: the microphone is the primary action, centred directly
// under the target word, so it stays visually dominant over the secondary
// audio/video buttons rather than growing or shrinking.
export const MicrophoneButton = styled.button<{ $isRecording: boolean }>(
  ({ theme, $isRecording }) => css`
    background-color: ${$isRecording ? theme.co.error50 : theme.co.tertiary0};
    color: ${theme.co.neutral99};
    border-radius: ${theme.bo.round};
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    width: ${theme.sz.s44};
    height: ${theme.sz.s44};
    font-size: ${theme.sz.s28};

    svg {
      font-size: 1em;
    }

    &:hover {
      background-color: ${$isRecording ? theme.co.error40 : theme.co.tertiary10};
    }

    ${$isRecording && recordingIndicator({ theme })};
  `,
);

export const MicrophoneWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s4};
  `,
);

export const MicrophoneHint = styled.div<{ $isHidden: boolean }>(
  ({ theme, $isHidden }) => css`
    margin-top: -${theme.sz.s2};
    min-height: ${theme.sz.s8};
    color: ${theme.co.tertiary0};
    ${theme.typography.heading.desktop.xxs};
    user-select: none;

    ${
      $isHidden &&
      css`
        visibility: hidden;
      `
    };

    @media (max-width: ${theme.sz.md}) {
      margin-top: 0;
    }
  `,
);

const CompactIconButton = styled.button(
  ({ theme }) => css`
    background: none;
    border-radius: ${theme.bo.round};
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;

    width: ${theme.sz.s16};
    height: ${theme.sz.s16};
    font-size: ${theme.sz.s8};

    svg {
      font-size: 1em;
    }
  `,
);

export const CompactAudioButton = styled(CompactIconButton)(
  ({ theme }) => css`
    background-color: ${theme.co.tertiary60};
    color: ${theme.co.secondary0};
    cursor: pointer;

    &:hover {
      background-color: ${theme.co.tertiary50};
    }
  `,
);

export const CompactMicrophoneButton = styled(CompactIconButton)<{ $isRecording: boolean }>(
  ({ theme, $isRecording }) => css`
    background-color: ${$isRecording ? theme.co.error50 : theme.co.tertiary0};
    color: ${theme.co.neutral99};
    cursor: pointer;

    &:hover {
      background-color: ${$isRecording ? theme.co.error40 : theme.co.tertiary10};
    }

    ${$isRecording && recordingIndicator({ theme })};
  `,
);

export const CircleElement = styled.div<{ $color: Color; $shrink?: boolean }>(
  ({ theme, $color }) => css`
    background-color: ${theme.co[$color]};
    border-radius: ${theme.bo.round};
    width: ${theme.sz.s44};
    height: ${theme.sz.s44};
    display: flex;
    justify-content: center;
    align-items: center;
  `,
);

export const AnalysisIcon = styled(Icon)<{ $color: Color }>(
  ({ theme, $color }) => css`
    color: ${theme.co[$color]};
    font-size: ${theme.sz.s28};
  `,
);

export const CompactCircleElement = styled(CircleElement)(
  ({ theme }) => css`
    width: ${theme.sz.s16};
    height: ${theme.sz.s16};
  `,
);

export const CompactAnalysisIcon = styled(AnalysisIcon)(
  ({ theme }) => css`
    font-size: ${theme.sz.s8};
  `,
);
